import React, { useState, useEffect } from 'react';
import { Share, Monitor, Smartphone, Download, PlayCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const InstallAppPage = () => {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    ) {
      setIsStandalone(true);
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    const android = /android/.test(userAgent);
    const desktop = !ios && !android;

    setIsIOS(ios);
    setIsAndroid(android);
    setIsDesktop(desktop);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the A2HS prompt');
      } else {
        console.log('User dismissed the A2HS prompt');
      }
      setDeferredPrompt(null);
    } else {
      alert(
        'Seu navegador atual já instalou o aplicativo ou não suporta a instalação direta. Siga as instruções abaixo para adicionar à tela inicial.'
      );
    }
  };

  const InstructionStep = ({ number, title, description, icon: Icon }) => (
    <div className="flex items-start mb-6">
      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 font-bold mr-4">
        {number}
      </div>
      <div>
        <h4 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          {title}
          {Icon && <Icon size={18} className="text-gray-500" />}
        </h4>
        <p className="mt-1 text-gray-600">{description}</p>
      </div>
    </div>
  );

  const IOSInstructions = () => (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center gap-2">
        <Smartphone className="text-purple-600" /> Instalação no iPhone / iPad
      </h3>

      <InstructionStep
        number="1"
        title="Abra no Safari"
        description="Certifique-se de estar usando o navegador Safari."
      />
      <InstructionStep
        number="2"
        title="Toque em Compartilhar"
        description="Procure o ícone de compartilhar na barra inferior do navegador."
        icon={Share}
      />
      <InstructionStep
        number="3"
        title="Adicionar à Tela de Início"
        description="Role as opções para baixo, encontre e toque em 'Adicionar à Tela de Início' e depois em 'Adicionar'."
      />

      <div className="mt-8 border-t border-gray-200 pt-6">
        <div className="flex items-center gap-2 mb-3">
          <PlayCircle className="text-purple-600" size={20} />
          <h4 className="text-base font-semibold text-gray-900">
            Vídeo tutorial
          </h4>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Assista ao passo a passo de instalação no iPhone:
        </p>

        <div className="mx-auto w-full max-w-[320px] rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-black">
          <div className="aspect-[9/16]">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/wiLV9IWGvY8"
              title="Tutorial de instalação do OdontoAssist no iPhone"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        <div className="mt-3 text-center">
          <a
            href="https://youtube.com/shorts/wiLV9IWGvY8?feature=share"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-purple-700 hover:text-purple-900"
          >
            Abrir vídeo no YouTube
          </a>
        </div>
      </div>
    </div>
  );

  const AndroidInstructions = () => (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center gap-2">
        <Smartphone className="text-purple-600" /> Instalação no Android
      </h3>
      <InstructionStep
        number="1"
        title="Abra no Chrome"
        description="Certifique-se de estar usando o navegador Google Chrome."
      />
      <InstructionStep
        number="2"
        title="Pelo menu do navegador"
        description="Toque nos 3 pontinhos no canto superior direito do Chrome e selecione 'Instalar aplicativo' ou 'Adicionar à tela inicial'."
      />
      <div className="mt-8 border-t border-gray-200 pt-6">
        {deferredPrompt ? (
          <button
            onClick={handleInstallClick}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            Instalar Aplicativo Agora
          </button>
        ) : (
          <p className="text-sm text-gray-500 text-center">
            Você também pode usar o menu do navegador acima para instalar manualmente.
          </p>
        )}
      </div>
    </div>
  );

  const content = (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl w-full space-y-8 bg-white p-8 rounded-xl shadow-lg border border-gray-100">
        <div className="text-center">
          <div className="mx-auto h-20 w-20 bg-purple-100 rounded-2xl flex items-center justify-center mb-4 shadow-sm border border-purple-200">
            <Download className="h-10 w-10 text-purple-600" />
          </div>
          <h2 className="mt-2 text-2xl tracking-tight font-bold text-gray-900">
            Instale o OdontoAssist no seu celular
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Tenha acesso rápido e fácil a todos os seus dados diretamente da tela de início do seu dispositivo, sem precisar baixar nada nas lojas de aplicativos.
          </p>
        </div>

        {isStandalone ? (
          <div className="rounded-md bg-green-50 border border-green-200 p-4 mt-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <Smartphone className="h-5 w-5 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Aplicativo já instalado
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Parece que você já está usando a versão instalada do OdontoAssist. Aproveite!</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => window.location.href = '/'}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 font-medium text-sm transition"
                  >
                    Ir para o painel principal
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8">
            {isIOS && <IOSInstructions />}

            {isAndroid && <AndroidInstructions />}

            {isDesktop && (
              <>
                <div className="rounded-md bg-blue-50 border border-blue-200 p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Monitor className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-blue-900">
                        Você está acessando pelo computador
                      </h3>
                      <p className="mt-1 text-sm text-blue-700">
                        No computador não há instalação como no celular. Abaixo estão as instruções para iPhone/iPad e Android.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <IOSInstructions />
                  <AndroidInstructions />
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm font-medium text-purple-600 hover:text-purple-500"
          >
            Voltar para o sistema
          </button>
        </div>
      </div>
    </div>
  );

  if (user) {
    return <Layout>{content}</Layout>;
  }

  return content;
};

export default InstallAppPage;